import { acquireBodyScrollLock, claimOverlay } from './foundation.js';

const instances = new WeakMap();
const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function nextDisclosureState(open, action) {
  return action === 'activate' ? !open : false;
}

export function nextMobilePath(path, action, id) {
  if (action === 'push') return [...path, id];
  if (action === 'back') return path.slice(0, -1);
  return [];
}

export function nextStickyState(mode, previousY, currentY, visible) {
  if (mode === 'off') return false;
  if (mode === 'always' || currentY < 72) return true;
  if (Math.abs(currentY - previousY) < 6) return visible;
  return currentY < previousY;
}

// Header language toggle (snippets/language-toggle.liquid). The server href moves the path to the other language's root;
// when the link is used, Shopify's rel="alternate" hreflang URL for this page replaces it (translated handles, no redirect)
// and the current query string and hash are kept (filters and sort pushed with pushState, search terms, variant, preview).
// Only the alternate's path is used, so the link stays on this host (preview, theme dev proxy), unless the other language
// lives on another domain (data-language-domain: both languages share the root "/").
export function languageToggleHref(serverHref, alternateHref, search = '', hash = '', origin = 'http://localhost', otherDomain = false) {
  const here = new URL(origin);
  let target;
  try { target = new URL(serverHref || '/', here); } catch { target = new URL('/', here); }
  if (alternateHref) {
    try { target = new URL(alternateHref, here); } catch { /* keep the server path */ }
  }
  const prefix = otherDomain && target.origin !== here.origin ? target.origin : '';
  return `${prefix}${target.pathname}${search || ''}${hash || ''}`;
}

// A path inside a language root: '/' holds every path; '/ar' holds '/ar' and '/ar/…' but not another market's '/ar-ae/…'.
function insideLanguageRoot(pathname, root) {
  const base = (root || '/').replace(/\/+$/, '');
  return !base || pathname === base || pathname.startsWith(`${base}/`);
}

// Shopify prints a rel="alternate" link per language ("ar": the primary market) and per market language ("ar-AE"). The
// shopper's market tag is tried first, and an alternate outside the other language's root (another market) is skipped,
// so a shopper in a secondary market stays in it; nothing usable → undefined (the server href stays).
export function pickLanguageAlternate(alternates, lang, country = '', targetRoot = '/', origin = 'http://localhost') {
  const tags = [lang && country && `${lang}-${country}`, lang].filter(Boolean).map((tag) => tag.toLowerCase());
  for (const tag of tags) {
    for (const alternate of alternates || []) {
      if (String(alternate?.hreflang || '').toLowerCase() !== tag) continue;
      try {
        if (insideLanguageRoot(new URL(alternate.href, origin).pathname, targetRoot)) return alternate.href;
      } catch { /* a broken alternate is skipped */ }
    }
  }
  return undefined;
}

export function mountHeader(root) {
  if (instances.has(root)) return instances.get(root);
  const controller = new AbortController();
  const { signal } = controller;
  const drawer = root.querySelector('[data-mobile-drawer]');
  const overlay = root.querySelector('[data-mobile-overlay]');
  const openButton = root.querySelector('[data-mobile-open]');
  const closeButton = root.querySelector('[data-mobile-close]');
  const levels = [...root.querySelectorAll('[data-mobile-level]')];
  const desktopToggles = [...root.querySelectorAll('[data-submenu-toggle]')];
  const languageLinks = [...root.querySelectorAll('[data-language-toggle]')];
  let path = [];
  let returnFocus = null;
  let opener = null;
  let releaseScroll = null;
  let releaseOverlay = null;
  let drawerOpen = false;
  let previousY = window.scrollY;
  let ticking = false;

  // The header stays in flow (below any announcement bar); CSS pulls the first hero up under it by the
  // measured header height, published on <html> so the hero can pad its copy by the same amount.
  function syncHomeOverlay() {
    const firstSection = document.querySelector('main#MainContent > .shopify-section');
    const firstHero = firstSection?.querySelector('[data-home-transparent-hero="true"]');
    const eligible = document.body.classList.contains('template-index') && root.dataset.transparentHero === 'true' && Boolean(firstHero);
    const html = document.documentElement;
    root.dataset.homeOverlay = String(eligible);
    if (eligible) {
      html.dataset.headerOverlay = 'true';
      html.style.setProperty('--header-overlay-offset', `${root.offsetHeight}px`);
    } else clearOverlayOffset();
    root.dataset.homeOverlayActive = String(eligible && window.scrollY < root.offsetHeight);
  }

  // Sticky elements below the header (the collection toolbar) stop under it: publish the height of the sticky header group
  // (the whole header group) on <html> as --header-sticky-offset; 0 when the header does not stick or is hidden on scroll.
  const stickyGroup = root.closest('.shopify-section-group-header-group') || root;
  function syncStickyOffset() {
    const visible = root.dataset.sticky !== 'off' && root.dataset.stickyVisible !== 'false';
    document.documentElement.style.setProperty('--header-sticky-offset', visible ? `${Math.round(stickyGroup.getBoundingClientRect().height)}px` : '0px');
  }

  function clearOverlayOffset() {
    const html = document.documentElement;
    if (!html.dataset.headerOverlay) return;
    delete html.dataset.headerOverlay;
    html.style.removeProperty('--header-overlay-offset');
  }

  function syncLanguageLinks() {
    if (!languageLinks.length) return;
    const alternates = [...document.querySelectorAll('link[rel="alternate"][hreflang]')].map((tag) => ({ hreflang: tag.getAttribute('hreflang'), href: tag.href }));
    for (const link of languageLinks) {
      link.dataset.serverHref ??= link.getAttribute('href');
      const alternate = pickLanguageAlternate(alternates, link.getAttribute('hreflang') || '', link.dataset.country || '', link.dataset.targetRoot || '/', location.origin);
      link.setAttribute('href', languageToggleHref(link.dataset.serverHref, alternate, location.search, location.hash, location.origin, link.hasAttribute('data-language-domain')));
    }
  }

  function closeMenus(except = null, restore = false) {
    const focusReturn = restore ? desktopToggles.find((button) => button.getAttribute('aria-expanded') === 'true' && !button.closest('.header-nav__panel')) : null;
    for (const button of desktopToggles) {
      if (button === except) continue;
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) continue;
      if (except && panel.contains(except)) continue;
      button.setAttribute('aria-expanded', 'false');
      panel.removeAttribute('data-open');
    }
    focusReturn?.focus();
  }

  function setMenu(button, open) {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    closeMenus(button);
    button.setAttribute('aria-expanded', String(open));
    panel.toggleAttribute('data-open', open);
  }

  function showLevel() {
    const current = path.at(-1);
    for (const level of levels) level.hidden = level.dataset.mobileLevel !== (current || 'root');
    const active = levels.find((level) => !level.hidden);
    active?.querySelector('[data-mobile-back],a,button')?.focus();
  }

  function openDrawer(trigger = openButton) {
    if (drawerOpen) return;
    closeMenus();
    releaseOverlay = claimOverlay(() => closeDrawer(false));
    drawerOpen = true;
    path = nextMobilePath(path, 'reset');
    showLevel();
    opener = trigger || openButton;
    returnFocus = opener || document.activeElement;
    releaseScroll = acquireBodyScrollLock();
    drawer.hidden = false;
    overlay.hidden = false;
    opener?.setAttribute('aria-expanded', 'true');
    closeButton?.focus();
  }

  function closeDrawer(restore = true) {
    if (!drawerOpen) return;
    drawerOpen = false;
    drawer.hidden = true;
    overlay.hidden = true;
    releaseScroll?.();
    releaseScroll = null;
    releaseOverlay?.();
    releaseOverlay = null;
    opener?.setAttribute('aria-expanded', 'false');
    path = nextMobilePath(path, 'reset');
    for (const level of levels) level.hidden = level.dataset.mobileLevel !== 'root';
    if (restore && returnFocus?.isConnected) returnFocus.focus();
  }

  function trap(event) {
    if (!drawerOpen || event.key !== 'Tab') return;
    const items = [...drawer.querySelectorAll(focusable)].filter((item) => !item.closest('[hidden]'));
    const first = items[0];
    const last = items.at(-1);
    if (!first) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-language-toggle]')) syncLanguageLinks();
    const button = event.target.closest('[data-submenu-toggle]');
    if (button && root.contains(button)) {
      setMenu(button, nextDisclosureState(button.getAttribute('aria-expanded') === 'true', 'activate'));
      return;
    }
    const menuOpener = event.target.closest('[data-mobile-open]');
    if (menuOpener) openDrawer(menuOpener);
    if (event.target.closest('[data-mobile-close], [data-mobile-overlay]')) closeDrawer();
    const next = event.target.closest('[data-mobile-target]');
    if (next) {
      path = nextMobilePath(path, 'push', next.dataset.mobileTarget);
      showLevel();
    }
    if (event.target.closest('[data-mobile-back]')) {
      path = nextMobilePath(path, 'back');
      showLevel();
    }
  }, { signal });

  // The URL can change after load (filters, sort, variant): refresh the language link before it is used or copied.
  for (const type of ['pointerdown', 'focusin']) {
    root.addEventListener(type, (event) => { if (event.target.closest?.('[data-language-toggle]')) syncLanguageLinks(); }, { signal });
  }
  window.addEventListener('popstate', syncLanguageLinks, { signal });
  syncLanguageLinks();

  root.addEventListener('pointerover', (event) => {
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const item = event.target.closest('[data-menu-item]');
    if (!item || !root.contains(item) || item.contains(event.relatedTarget)) return;
    const button = item.querySelector(':scope > [data-submenu-toggle]');
    if (button) setMenu(button, true);
  }, { signal });
  root.addEventListener('pointerout', (event) => {
    const item = event.target.closest('[data-menu-item]');
    if (!item || item.contains(event.relatedTarget)) return;
    const button = item.querySelector(':scope > [data-submenu-toggle]');
    if (button) setMenu(button, false);
  }, { signal });

  document.addEventListener('pointerdown', (event) => {
    if (!root.contains(event.target)) closeMenus();
  }, { signal });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (drawerOpen) closeDrawer();
      else closeMenus(null, true);
    }
    trap(event);
  }, { signal });
  document.addEventListener('focusin', (event) => {
    if (drawerOpen && !drawer.contains(event.target)) closeButton?.focus();
  }, { signal });

  function updateSticky() {
    ticking = false;
    const y = window.scrollY;
    root.dataset.stickyVisible = String(nextStickyState(root.dataset.sticky, previousY, y, root.dataset.stickyVisible === 'true'));
    syncHomeOverlay();
    syncStickyOffset();
    previousY = y;
  }
  if (root.dataset.sticky !== 'off') {
    root.dataset.stickyVisible = 'true';
    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateSticky);
      }
    }, { passive: true, signal });
  }
  else window.addEventListener('scroll', syncHomeOverlay, { passive: true, signal });
  syncHomeOverlay();
  syncStickyOffset();
  const stickyResize = new ResizeObserver(syncStickyOffset);
  stickyResize.observe(stickyGroup);
  signal.addEventListener('abort', () => stickyResize.disconnect());
  window.addEventListener('resize', syncHomeOverlay, { passive: true, signal });
  document.addEventListener('shopify:section:load', syncHomeOverlay, { signal });
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(syncHomeOverlay), { signal });

  const api = { close: () => { closeMenus(); closeDrawer(false); }, openMega: (block) => {
    const selected = root.querySelector(`[data-mega-block="${CSS.escape(block)}"], [data-block-id="${CSS.escape(block)}"]`);
    const panel = selected?.closest('.header-nav__panel');
    const button = panel && root.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`);
    if (button) setMenu(button, true);
  }, destroy: () => {
    closeMenus();
    closeDrawer(false);
    clearOverlayOffset();
    controller.abort();
    instances.delete(root);
  } };
  instances.set(root, api);
  return api;
}

function findHeaders(scope) {
  return [...(scope.matches?.('[data-header-root]') ? [scope] : []), ...scope.querySelectorAll('[data-header-root]')];
}

if (typeof document !== 'undefined') {
  findHeaders(document).forEach(mountHeader);
  document.addEventListener('shopify:section:load', (event) => findHeaders(event.target).forEach(mountHeader));
  document.addEventListener('shopify:section:unload', (event) => findHeaders(event.target).forEach((root) => instances.get(root)?.destroy()));
  document.addEventListener('shopify:section:select', (event) => findHeaders(event.target).forEach((root) => { root.dataset.stickyVisible = 'true'; instances.get(root)?.close(); }));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-header-root]');
    if (root) instances.get(root)?.openMega(event.detail.blockId);
  });
}
